import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useFeaturesManifest } from './FeaturesPage';
import './App.css';

const CONTENT_BASE = '/docs-content/features/';

export default function FeatureDetailPage() {
  const { slug } = useParams();
  const { manifest, error: manifestError } = useFeaturesManifest();
  const [body, setBody] = useState(null);
  const [bodyError, setBodyError] = useState(null);

  const feature = useMemo(
    () => manifest?.features?.find((f) => f.slug === slug) || null,
    [manifest, slug]
  );

  const siblings = useMemo(() => {
    if (!manifest || !feature) return { prev: null, next: null };
    const list = manifest.features;
    const i = list.findIndex((f) => f.slug === feature.slug);
    return { prev: i > 0 ? list[i - 1] : null, next: i < list.length - 1 ? list[i + 1] : null };
  }, [manifest, feature]);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  useEffect(() => {
    if (!feature) return undefined;
    let cancelled = false;
    setBody(null);
    setBodyError(null);
    fetch(`${CONTENT_BASE}${feature.doc}`, { cache: 'no-cache' })
      .then((r) => {
        if (!r.ok) throw new Error(`${feature.doc} (${r.status})`);
        return r.text();
      })
      .then((t) => { if (!cancelled) setBody(t); })
      .catch((e) => { if (!cancelled) setBodyError(e.message || String(e)); });
    return () => { cancelled = true; };
  }, [feature]);

  const components = useMemo(() => ({
    h1: ({ children }) => (
      <h1 style={{ fontSize: '2.4rem', fontWeight: 800, color: '#111827', margin: '0 0 14px', lineHeight: 1.12 }}>{children}</h1>
    ),
    h2: ({ children }) => (
      <h2 style={{ fontSize: '1.3rem', fontWeight: 700, color: '#111827', margin: '40px 0 14px' }}>{children}</h2>
    ),
    p: ({ children }) => (
      <p style={{ color: '#374151', lineHeight: 1.75, marginBottom: 16, fontSize: '1rem' }}>{children}</p>
    ),
    ul: ({ children }) => (
      <ul style={{ color: '#374151', lineHeight: 1.75, marginBottom: 18, paddingLeft: 22 }}>{children}</ul>
    ),
    ol: ({ children }) => (
      <ol style={{ color: '#374151', lineHeight: 1.75, marginBottom: 18, paddingLeft: 22 }}>{children}</ol>
    ),
    li: ({ children }) => <li style={{ marginBottom: 8 }}>{children}</li>,
    img: ({ src, alt }) => (
      <span style={{ display: 'block', margin: '8px 0 32px' }}>
        <img
          src={src}
          alt={alt || ''}
          onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }}
          style={{
            width: '100%', display: 'block', borderRadius: 12,
            border: '1px solid #e5e7eb', boxShadow: '0 14px 36px rgba(15,23,42,0.12)',
            background: '#0f172a',
          }}
        />
      </span>
    ),
    code: ({ inline, children, ...rest }) => (
      inline
        ? <code style={{ background: '#f3f4f6', padding: '1px 6px', borderRadius: 4, fontSize: '0.9em', color: '#111827' }} {...rest}>{children}</code>
        : <code {...rest}>{children}</code>
    ),
    pre: ({ children }) => (
      <pre style={{
        background: '#0f172a', color: '#e2e8f0', padding: 16, borderRadius: 8,
        overflowX: 'auto', fontSize: '0.85rem', lineHeight: 1.6, marginBottom: 22,
      }}>{children}</pre>
    ),
    strong: ({ children }) => <strong style={{ color: '#111827', fontWeight: 700 }}>{children}</strong>,
  }), []);

  const notFound = manifest && !feature;

  return (
    <div className="mp-page" style={{ padding: 0, background: '#fff' }}>
      <div style={{ maxWidth: 820, margin: '0 auto', padding: '40px 24px 96px' }}>
        <Link
          to="/features"
          style={{ fontSize: '0.86rem', color: '#6b7280', textDecoration: 'none', fontWeight: 600 }}
        >
          ← All features
        </Link>

        {manifestError && (
          <div style={{ marginTop: 24, padding: 16, borderRadius: 8, background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b' }}>
            Could not load the feature list: {manifestError}
          </div>
        )}

        {notFound && (
          <div style={{ marginTop: 48 }}>
            <h1 style={{ fontSize: '1.8rem', fontWeight: 800, color: '#111827', margin: '0 0 12px' }}>
              No feature called “{slug}”
            </h1>
            <p style={{ color: '#4b5563' }}>
              It may have been renamed. <Link to="/features" style={{ color: '#3b82f6' }}>Browse all features</Link>.
            </p>
          </div>
        )}

        {feature && (
          <>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '22px 0 4px' }}>
              <span style={{
                fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
                padding: '4px 8px', borderRadius: 4, color: '#fff', background: feature.accent,
              }}>
                {feature.title}
              </span>
              {feature.experimental && (
                <span style={{
                  fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
                  padding: '4px 8px', borderRadius: 4, background: '#faf5ff', color: '#7e22ce', border: '1px solid #e9d5ff',
                }}>Experimental</span>
              )}
              {feature.permission && (
                <span style={{
                  fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
                  padding: '4px 8px', borderRadius: 4, background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0',
                }}>Needs {feature.permission}</span>
              )}
            </div>

            {bodyError && (
              <p style={{ color: '#991b1b', marginTop: 24 }}>
                This page’s content failed to load ({bodyError}).
              </p>
            )}
            {!body && !bodyError && <p style={{ color: '#6b7280', marginTop: 32 }}>Loading…</p>}
            {body && (
              <article style={{ marginTop: 10 }}>
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>{body}</ReactMarkdown>
              </article>
            )}

            <div style={{
              marginTop: 48, padding: '24px 26px', borderRadius: 12,
              background: '#f9fafb', border: '1px solid #e5e7eb',
              display: 'flex', gap: 16, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap',
            }}>
              <div>
                <div style={{ fontWeight: 700, color: '#111827', marginBottom: 4 }}>
                  {feature.title} ships with mChatAI+
                </div>
                <div style={{ color: '#6b7280', fontSize: '0.88rem' }}>
                  Native macOS app. Your own API keys, or fully on-device.
                </div>
              </div>
              <Link to="/macos-app" className="mp-cta-button" style={{ textDecoration: 'none', whiteSpace: 'nowrap' }}>
                Get mChatAI+
              </Link>
            </div>

            <nav style={{
              marginTop: 32, display: 'flex', justifyContent: 'space-between', gap: 16,
              borderTop: '1px solid #e5e7eb', paddingTop: 20, fontSize: '0.88rem',
            }}>
              {siblings.prev ? (
                <Link to={`/features/${siblings.prev.slug}`} style={{ color: '#3b82f6', textDecoration: 'none', fontWeight: 600 }}>
                  ← {siblings.prev.title}
                </Link>
              ) : <span />}
              {siblings.next && (
                <Link to={`/features/${siblings.next.slug}`} style={{ color: '#3b82f6', textDecoration: 'none', fontWeight: 600, marginLeft: 'auto' }}>
                  {siblings.next.title} →
                </Link>
              )}
            </nav>
          </>
        )}
      </div>
    </div>
  );
}
